import {
  Typography,
  Box,
  Container,
  CircularProgress,
  Button,
  Alert,
} from "@mui/material";
import { useEffect, useState, useMemo } from "react";
import { useTheme, useMediaQuery } from "@mui/material";
import { useHighLevelContext } from "../../../hooks/HighLevelContext";
import { usePlanContext } from "../../../contexts/PlanContext";
import { EnhancedMembershipsList } from "../../../components/memberships-list/enhanced-memberships-list";
import "./styles.scss";

export type PlanKey = "basic" | "Guild" | "GuildPlus";

export const HomeownerPlanSelectionFieldset = ({
  isValid,
  step,
  setNextStep,
  setStep,
  selectedPlan,
  setSelectedPlan,
  setBillingAmount,
  setSelectedPlanData,
  allowedPlanKeys,
  availabilityMessage,
}: {
  isValid: boolean;
  step: number;
  setNextStep: () => void;
  setStep: (value: number) => void;
  selectedPlan: PlanKey | null;
  setSelectedPlan: (val: PlanKey) => void;
  setBillingAmount: (value: number) => void;
  setSelectedPlanData?: (data: any) => void;
  allowedPlanKeys?: PlanKey[];
  availabilityMessage?: string | null;
}) => {
  const { getPlanList } = useHighLevelContext();
  const [selectedPlanId, setSelectedPlanId] = useState<string | null>(null);
  const [plansData, setPlansData] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  
  // Get plan context
  const planContext = usePlanContext();
  
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  useEffect(() => {
    const fetchPlans = async () => {
      setLoading(true);
      try {
        const response = await getPlanList();
        
        // If no plans loaded from HighLevel, use mock plans for testing
        if (!response.items || response.items.length === 0) {
          const mockPlans = [
            {
              _id: 'mock-basic-monthly',
              name: 'Basic',
              description: '<p>Get started with our essential design resources and community access.</p><ul style="list-style-type: disc;"><li><p style="line-height: 25px;box-sizing: border-box;margin: 0px 0px 0px 16px;font-family: Jost;font-size: 16px;color: rgb(55, 65, 81);font-weight: 400;" class="MuiTypography-root MuiTypography-body1 css-10kxm5a">Basic Project Gallery</p></li><li><p style="line-height: 25px;box-sizing: border-box;margin: 0px 0px 0px 16px;font-family: Jost;font-size: 16px;color: rgb(55, 65, 81);font-weight: 400;" class="MuiTypography-root MuiTypography-body1 css-10kxm5a">Community Access</p></li><li><p style="line-height: 25px;box-sizing: border-box;margin: 0px 0px 0px 16px;font-family: Jost;font-size: 16px;color: rgb(55, 65, 81);font-weight: 400;" class="MuiTypography-root MuiTypography-body1 css-10kxm5a">Basic Filters</p></li></ul>',
              pricing: { price: 0, period: 'monthly' },
              metadata: { tier: 'Basic' },
              perks: [],
              benefits: []
            },
            {
              _id: 'mock-guild-monthly',
              name: 'Guild',
              description: '<p>Ready to see what we are all about? The Guild level gives you full access to our detailed real-world projects from our vetted designers, along with full access to our educational articles.</p><ul style="list-style-type: disc;"><li><p style="line-height: 25px;box-sizing: border-box;margin: 0px 0px 0px 16px;font-family: Jost;font-size: 16px;color: rgb(55, 65, 81);font-weight: 400;" class="MuiTypography-root MuiTypography-body1 css-10kxm5a">Curated Project Gallery</p></li><li><p style="line-height: 25px;box-sizing: border-box;margin: 0px 0px 0px 16px;font-family: Jost;font-size: 16px;color: rgb(55, 65, 81);font-weight: 400;" class="MuiTypography-root MuiTypography-body1 css-10kxm5a">Guild Guide Articles</p></li><li><p style="line-height: 25px;box-sizing: border-box;margin: 0px 0px 0px 16px;font-family: Jost;font-size: 16px;color: rgb(55, 65, 81);font-weight: 400;" class="MuiTypography-root MuiTypography-body1 css-10kxm5a">Powerful Filters</p></li><li><p style="line-height: 25px;box-sizing: border-box;margin: 0px 0px 0px 16px;font-family: Jost;font-size: 16px;color: rgb(55, 65, 81);font-weight: 400;" class="MuiTypography-root MuiTypography-body1 css-10kxm5a">Create &amp; Save to Idea Books</p></li></ul>',
              pricing: { price: 4.99, period: 'monthly' },
              metadata: { tier: 'Guild' },
              perks: [],
              benefits: []
            },
            {
              _id: 'mock-guild-plus-annually',
              name: 'Guild Plus',
              description: '<p>Premium access with exclusive benefits and advanced features.</p><ul style="list-style-type: disc;"><li><p style="line-height: 25px;box-sizing: border-box;margin: 0px 0px 0px 16px;font-family: Jost;font-size: 16px;color: rgb(55, 65, 81);font-weight: 400;" class="MuiTypography-root MuiTypography-body1 css-10kxm5a">Everything in Guild</p></li><li><p style="line-height: 25px;box-sizing: border-box;margin: 0px 0px 0px 16px;font-family: Jost;font-size: 16px;color: rgb(55, 65, 81);font-weight: 400;" class="MuiTypography-root MuiTypography-body1 css-10kxm5a">Priority Designer Matching</p></li><li><p style="line-height: 25px;box-sizing: border-box;margin: 0px 0px 0px 16px;font-family: Jost;font-size: 16px;color: rgb(55, 65, 81);font-weight: 400;" class="MuiTypography-root MuiTypography-body1 css-10kxm5a">Exclusive Design Consultations</p></li><li><p style="line-height: 25px;box-sizing: border-box;margin: 0px 0px 0px 16px;font-family: Jost;font-size: 16px;color: rgb(55, 65, 81);font-weight: 400;" class="MuiTypography-root MuiTypography-body1 css-10kxm5a">Advanced Project Analytics</p></li></ul>',
              pricing: { price: 149.99, period: 'annually' },
              metadata: { tier: 'Guild+' },
              perks: [],
              benefits: []
            }
          ];
          setPlansData(mockPlans);
        } else {
          setPlansData(response.items || []);
        }
      } catch (error) {
        // Use mock plans as fallback
        const mockPlans = [
          {
            _id: 'mock-basic-monthly',
            name: 'Basic',
            description: '<p>Get started with our essential design resources and community access.</p><ul style="list-style-type: disc;"><li><p style="line-height: 25px;box-sizing: border-box;margin: 0px 0px 0px 16px;font-family: Jost;font-size: 16px;color: rgb(55, 65, 81);font-weight: 400;" class="MuiTypography-root MuiTypography-body1 css-10kxm5a">Basic Project Gallery</p></li><li><p style="line-height: 25px;box-sizing: border-box;margin: 0px 0px 0px 16px;font-family: Jost;font-size: 16px;color: rgb(55, 65, 81);font-weight: 400;" class="MuiTypography-root MuiTypography-body1 css-10kxm5a">Community Access</p></li><li><p style="line-height: 25px;box-sizing: border-box;margin: 0px 0px 0px 16px;font-family: Jost;font-size: 16px;color: rgb(55, 65, 81);font-weight: 400;" class="MuiTypography-root MuiTypography-body1 css-10kxm5a">Basic Filters</p></li></ul>',
            pricing: { price: 0, period: 'monthly' },
            metadata: { tier: 'Basic' },
            perks: [],
            benefits: []
          },
          {
            _id: 'mock-guild-monthly',
            name: 'Guild',
            description: '<p>Ready to see what we are all about? The Guild level gives you full access to our detailed real-world projects from our vetted designers, along with full access to our educational articles.</p><ul style="list-style-type: disc;"><li><p style="line-height: 25px;box-sizing: border-box;margin: 0px 0px 0px 16px;font-family: Jost;font-size: 16px;color: rgb(55, 65, 81);font-weight: 400;" class="MuiTypography-root MuiTypography-body1 css-10kxm5a">Curated Project Gallery</p></li><li><p style="line-height: 25px;box-sizing: border-box;margin: 0px 0px 0px 16px;font-family: Jost;font-size: 16px;color: rgb(55, 65, 81);font-weight: 400;" class="MuiTypography-root MuiTypography-body1 css-10kxm5a">Guild Guide Articles</p></li><li><p style="line-height: 25px;box-sizing: border-box;margin: 0px 0px 0px 16px;font-family: Jost;font-size: 16px;color: rgb(55, 65, 81);font-weight: 400;" class="MuiTypography-root MuiTypography-body1 css-10kxm5a">Powerful Filters</p></li><li><p style="line-height: 25px;box-sizing: border-box;margin: 0px 0px 0px 16px;font-family: Jost;font-size: 16px;color: rgb(55, 65, 81);font-weight: 400;" class="MuiTypography-root MuiTypography-body1 css-10kxm5a">Create &amp; Save to Idea Books</p></li></ul>',
            pricing: { price: 4.99, period: 'monthly' },
            metadata: { tier: 'Guild' },
            perks: [],
            benefits: []
          },
          {
            _id: 'mock-guild-plus-annually',
            name: 'Guild Plus',
            description: '<p>Premium access with exclusive benefits and advanced features.</p><ul style="list-style-type: disc;"><li><p style="line-height: 25px;box-sizing: border-box;margin: 0px 0px 0px 16px;font-family: Jost;font-size: 16px;color: rgb(55, 65, 81);font-weight: 400;" class="MuiTypography-root MuiTypography-body1 css-10kxm5a">Everything in Guild</p></li><li><p style="line-height: 25px;box-sizing: border-box;margin: 0px 0px 0px 16px;font-family: Jost;font-size: 16px;color: rgb(55, 65, 81);font-weight: 400;" class="MuiTypography-root MuiTypography-body1 css-10kxm5a">Priority Designer Matching</p></li><li><p style="line-height: 25px;box-sizing: border-box;margin: 0px 0px 0px 16px;font-family: Jost;font-size: 16px;color: rgb(55, 65, 81);font-weight: 400;" class="MuiTypography-root MuiTypography-body1 css-10kxm5a">Exclusive Design Consultations</p></li><li><p style="line-height: 25px;box-sizing: border-box;margin: 0px 0px 0px 16px;font-family: Jost;font-size: 16px;color: rgb(55, 65, 81);font-weight: 400;" class="MuiTypography-root MuiTypography-body1 css-10kxm5a">Advanced Project Analytics</p></li></ul>',
            pricing: { price: 149.99, period: 'annually' },
            metadata: { tier: 'Guild+' },
            perks: [],
            benefits: []
          }
        ];
        setPlansData(mockPlans);
      } finally {
        setLoading(false);
      }
    };
    fetchPlans();
  }, [getPlanList]);

  // Helper function to extract features from plan data
  const extractFeaturesFromPlan = (plan: any): string[] => {
    // First try: plan.perks.values (if it's callable and returns an array)
    if (typeof plan?.perks?.values === 'function') {
      try {
        const values = Array.from(plan.perks.values());
        if (Array.isArray(values)) {
          return values;
        }
      } catch (error) {
        console.warn('Error calling plan.perks.values():', error);
      }
    }
    // Second try: plan.benefits (if it's an array)
    else if (Array.isArray(plan?.benefits)) {
      return plan.benefits;
    }
    // Third try: plan.perks (if it's an array)
    else if (Array.isArray(plan?.perks)) {
      return plan.perks;
    }
    // Fourth try: plan.metadata.features
    else if (Array.isArray(plan?.metadata?.features)) {
      return plan.metadata.features;
    }
    // Fifth try: Extract from HTML description
    else if (plan?.description) {
      try {
        const tempDiv = document.createElement('div');
        tempDiv.innerHTML = plan.description;
        const listItems = tempDiv.querySelectorAll('li');
        const features: string[] = [];
        listItems.forEach(li => {
          const text = li.textContent?.trim();
          if (text) {
            features.push(text);
          }
        });
        return features;
      } catch (error) {
        return [];
      }
    }
    return [];
  };

  const resolvePlanKey = (plan: any): PlanKey | null => {
    const tierRaw = plan?.metadata?.tier || plan?.name || "";
    const tier = String(tierRaw).toLowerCase();
    if (tier.includes("basic")) return "basic";
    if (tier.includes("guild+") || tier.includes("guild plus") || tier.includes("guildplus")) {
      return "GuildPlus";
    }
    if (tier.includes("guild")) return "Guild";
    return null;
  };

  const allowedPlanSet = useMemo(
    () => new Set(allowedPlanKeys && allowedPlanKeys.length ? allowedPlanKeys : []),
    [allowedPlanKeys]
  );

  const visiblePlans = useMemo(() => {
    if (!allowedPlanKeys || allowedPlanKeys.length === 0) return plansData;
    return plansData.filter((plan) => {
      const key = resolvePlanKey(plan);
      return key ? allowedPlanSet.has(key) : false;
    });
  }, [allowedPlanKeys, allowedPlanSet, plansData]);

  useEffect(() => {
    if (!selectedPlanId) return;
    const exists = visiblePlans.some((plan) => plan._id === selectedPlanId);
    if (!exists) {
      setSelectedPlanId(null);
      setSelectedPlan(null as any);
      setBillingAmount(0);
    }
  }, [selectedPlanId, visiblePlans, setSelectedPlan, setBillingAmount]);

  // Handle plan selection from EnhancedMembershipsList
  const handlePlanSelect = (planId?: string) => {
    
    if (!planId) {
      setSelectedPlanId(null);
      setSelectedPlan(null as any);
      setBillingAmount(0);
      return;
    }

    const plan = plansData.find(p => p._id === planId);
    
    if (!plan) return;

    setSelectedPlanId(planId);
    
    // Map HighLevel plan to our PlanKey
    const tier = plan.metadata?.tier;
    const planKey = resolvePlanKey(plan) || 'basic';
    
    setSelectedPlan(planKey);
    
    // Set billing amount
    const price = plan.pricing?.price; // HighLevel stores price directly as number
    const amount = price ? Number(price) : 0;
    setBillingAmount(amount);

    // Store full plan data for payment component
    if (setSelectedPlanData) {
      const extractedFeatures = extractFeaturesFromPlan(plan);
      
      const planData = {
        id: plan._id,
        name: plan.name,
        tier: tier,
        price: amount,
        billingCycle: plan.pricing?.period || 'monthly',
        benefits: extractedFeatures,
        description: plan.description || '',
        metadata: plan.metadata
      };
      setSelectedPlanData(planData);
      
      // Also update plan context with proper Plan object
      const planForContext = {
        _id: plan._id,
        slug: plan.slug || plan.name?.toLowerCase().replace(/\s+/g, '-') || '',
        name: plan.name,
        description: plan.description || '',
        tagLine: plan.tagLine || plan.description || '',
        benefits: extractedFeatures,
        pricing: {
          price: amount,
          originalPrice: plan.pricing?.originalPrice || null,
          currency: plan.pricing?.currency || 'USD',
          period: plan.pricing?.period || 'monthly',
          singlePaymentForDuration: plan.pricing?.singlePaymentForDuration || false,
          subscription: plan.pricing?.subscription || null
        },
        public: plan.public || true,
        perks: extractedFeatures,
        primaryPlan: plan.primaryPlan || false,
        highlevelProductId: plan.highlevelProductId || '',
        highlevelPriceId: plan.highlevelPriceId || '',
        metadata: plan.metadata || {}
      };
      
      planContext.setSelectedPlan(planForContext);
      planContext.setBillingAmount(amount);
      planContext.setPlanType(tier);
      planContext.setBillingCycle(plan.pricing?.period === 'monthly' ? 'Monthly' : 'Annual');
      planContext.setHighlevelProductId(plan.highlevelProductId || null);
      planContext.setHighlevelPriceId(plan.highlevelPriceId || null);
    }
  };

  return (
    <Box 
      className="homeowner-plan-wrapper" 
      data-selected-plan={selectedPlan}
      sx={{
        backgroundColor: 'white',
        minHeight: '100vh',
        '@media (max-width: 768px)': {
          paddingBottom: '120px', // Add space for sticky button
        }
      }}
    >
      <Container maxWidth="lg">
        <Typography 
          variant="h4" 
          className="page-title font-title" 
          align="center"
          sx={{ 
            mb: 2,
            fontFamily: '"Cormorant Garamond" !important',
            fontSize: '41px',
            fontWeight: 700,
            lineHeight: 'normal',
            textTransform: 'capitalize',
            color: '#1C1C1C',
            '@media (max-width: 768px)': {
              fontSize: '32px',
            }
          }}
        >
          Choose Your Membership Plan
        </Typography>
        <Typography 
          className="page-subtitle" 
          align="center"
          sx={{ 
            mb: 4,
            fontSize: { xs: '1rem', sm: '1.125rem' },
            color: '#666',
            maxWidth: '600px',
            mx: 'auto'
          }}
        >
          Choose files-only or full Guild access. See what's included in each plan below.
        </Typography>

        {loading ? (
          <Box display="flex" justifyContent="center" mt={6}>
            <CircularProgress size={48} sx={{ color: '#00A06D' }} />
          </Box>
        ) : (
          <>
            {availabilityMessage ? (
              <Alert severity="info" sx={{ mb: 3 }}>
                <Typography variant="subtitle1" fontWeight={700} gutterBottom>
                  Coming Soon to Your Region
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: "pre-line" }}>
                  {availabilityMessage}
                </Typography>
              </Alert>
            ) : null}
            <EnhancedMembershipsList
              variant="homeowner"
              selectedPlan={selectedPlanId}
              onSelect={handlePlanSelect}
              showPlaceholders={false}
              plans={visiblePlans}
            />

            {/* Subscribe Button */}
            <Box sx={{ 
              display: 'flex', 
              flexDirection: 'column', 
              alignItems: 'center', 
              mt: 6,
              '@media (max-width: 768px)': {
                position: 'fixed',
                bottom: 0,
                left: 0,
                right: 0,
                backgroundColor: 'white !important',
                borderTop: '1px solid #e0e0e0',
                padding: '16px',
                zIndex: 1000,
                boxShadow: '0 -2px 10px rgba(0, 0, 0, 0.1)',
                mt: 0,
                minHeight: '80px', // Ensure minimum height for the container
                width: '100%',
                maxWidth: '100%',
              }
            }}>
              <Button
                variant="contained"
                size="large"
                disabled={!selectedPlan}
                onClick={() => {
                  // For Basic plan, skip payment and go directly to address form (step 5)
                  if (selectedPlan === 'basic') {
                    setStep(5); // Go directly to step 5 (address form)
                  } else {
                    // For paid plans (Guild/Guild+), proceed to payment
                    setNextStep();
                  }
                }}
                sx={{
                  backgroundColor: selectedPlan ? '#000' : '#ddd',
                  color: selectedPlan ? 'white' : '#888',
                  px: isMobile ? 6 : 12,
                  py: 1.5,
                  fontSize: isMobile ? '1rem' : '1.1rem',
                  fontWeight: 600,
                  borderRadius: 2,
                  textTransform: 'none',
                  mb: 3,
                  minWidth: isMobile ? '250px' : '300px',
                  width: isMobile ? '90%' : 'auto',
                  maxWidth: '400px',
                  cursor: selectedPlan ? 'pointer' : 'not-allowed',
                  '&:hover': {
                    backgroundColor: selectedPlan ? '#333' : '#ddd',
                  },
                  '&.Mui-disabled': {
                    backgroundColor: '#ddd',
                    color: '#888',
                  },
                  '@media (max-width: 768px)': {
                    width: '100%',
                    maxWidth: 'none',
                    minWidth: 'auto',
                    mb: 0,
                    backgroundColor: selectedPlan ? '#000' : '#ddd',
                    color: selectedPlan ? 'white' : '#888',
                    '&:hover': {
                      backgroundColor: selectedPlan ? '#333' : '#ddd',
                    },
                  }
                }}
              >
                {selectedPlan === 'basic' ? 'Continue' : 'Proceed to Payment'}
              </Button>
              
              {/* Terms and Privacy Policy */}
              <Typography 
                variant="body2" 
                sx={{ 
                  color: '#666',
                  fontSize: isMobile ? '0.8rem' : '0.875rem',
                  textAlign: 'center',
                  px: isMobile ? 2 : 0
                }}
              >
                By signing up you agree to{' '}
                <Typography 
                  component="a" 
                  href="/terms" 
                  sx={{ 
                    color: '#d22268',
                    textDecoration: 'none',
                    '&:hover': { textDecoration: 'underline' }
                  }}
                >
                  Terms
                </Typography>
                {' '}and{' '}
                <Typography 
                  component="a" 
                  href="/privacy-policy" 
                  sx={{ 
                    color: '#d22268',
                    textDecoration: 'none',
                    '&:hover': { textDecoration: 'underline' }
                  }}
                >
                  Privacy Policy
                </Typography>
              </Typography>
            </Box>
          </>
        )}
      </Container>
    </Box>
  );
};
